import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, Users, Phone, Search, Trash2, Edit2, 
  CheckCircle, XCircle, AlertTriangle, ChevronLeft, ChevronRight,
  Download, Eye, Tag, Calendar, RefreshCw, Loader2, FileText,
  Check, X, MoreHorizontal, Filter, Smartphone, MessageSquare, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contacts() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Files state
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesPagination, setFilesPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Contacts state (for selected file)
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsPagination, setContactsPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [contactsSearch, setContactsSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Import state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload, preview, duplicates, importing
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  
  // RCS check state
  const [rcsCheckLoading, setRcsCheckLoading] = useState(false);
  const [rcsCheckResults, setRcsCheckResults] = useState(null);
  const [rcsActionDialogOpen, setRcsActionDialogOpen] = useState(false);
  
  // Editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, column }
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);

  // Fetch contact files - using v2 API
  const fetchFiles = useCallback(async (page = 1) => {
    setFilesLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/files?page=${page}&limit=${filesPagination.limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setFiles(data.files);
        setFilesPagination(prev => ({ ...prev, ...data, page: data.page }));
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      toast.error('Erreur de chargement des fichiers');
    } finally {
      setFilesLoading(false);
    }
  }, [token, filesPagination.limit]);

  // Fetch contacts for selected file
  const fetchContacts = useCallback(async (fileId, page = 1, limit = rowsPerPage, search = '') => {
    setContactsLoading(true);
    try {
      let url = `${API_URL}/api/contacts/files/${fileId}?page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedFile(data);
        setContacts(data.contacts);
        setContactsPagination(data.pagination);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      toast.error('Erreur de chargement des contacts');
    } finally {
      setContactsLoading(false);
    }
  }, [token, rowsPerPage]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Handle file selection
  const handleSelectFile = (file) => {
    setContactsSearch('');
    fetchContacts(file.id);
  };

  // Handle file upload for preview
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['.csv', '.xlsx'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(ext)) {
      toast.error('Format non supporté. Utilisez CSV ou XLSX.');
      return;
    }
    
    setImportFile(file);
    setFileName(file.name.replace(/\.[^/.]+$/, ''));
    setImportLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/contacts/import/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportPreview(data);
        setImportStep('preview');
        
        if (!data.mobile_column_found) {
          toast.warning('Colonne "mobile" non trouvée. Vérifiez votre fichier.');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de lecture du fichier');
      }
    } catch (err) {
      console.error('Error previewing file:', err);
      toast.error('Erreur de lecture du fichier');
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import confirmation
  const handleImport = async (removeDuplicates) => {
    if (!importFile) return;
    
    setImportLoading(true);
    setDuplicateDialogOpen(false);
    setImportStep('importing');
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const params = new URLSearchParams({
        remove_duplicates: removeDuplicates.toString(),
        file_name: fileName || importFile.name
      });
      
      const response = await fetch(`${API_URL}/api/contacts/import?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.total_contacts} contacts importés avec succès`);
        setImportModalOpen(false);
        resetImportState();
        fetchFiles();
        
        // Auto-select the new file
        fetchContacts(data.file_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur d\'import');
        setImportStep('preview');
      }
    } catch (err) {
      console.error('Error importing:', err);
      toast.error('Erreur d\'import');
      setImportStep('preview');
    } finally {
      setImportLoading(false);
    }
  };

  // Check for duplicates and proceed
  const handleProceedImport = () => {
    if (importPreview?.duplicates_count > 0) {
      setDuplicateDialogOpen(true);
    } else {
      handleImport(false);
    }
  };

  // Reset import state
  const resetImportState = () => {
    setImportStep('upload');
    setImportFile(null);
    setImportPreview(null);
    setFileName('');
  };

  // Handle inline editing
  const startEditing = (rowIndex, column, currentValue) => {
    setEditingCell({ rowIndex, column });
    setEditValue(currentValue || '');
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const saveEdit = async () => {
    if (!editingCell || !selectedFile) return;
    
    const { rowIndex, column } = editingCell;
    
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/files/${selectedFile.id}/contacts/${rowIndex}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ [column]: editValue })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Update local state
        setContacts(prev => prev.map(c => 
          c.row_index === rowIndex 
            ? { ...c, ...data.contact }
            : c
        ));
        setSelectedFile(prev => ({
          ...prev,
          valid_contacts: data.valid_contacts,
          invalid_contacts: data.invalid_contacts
        }));
        toast.success('Sauvegardé', { duration: 1000 });
      }
    } catch (err) {
      console.error('Error saving:', err);
      toast.error('Erreur de sauvegarde');
    }
    
    setEditingCell(null);
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // Handle contact deletion
  const handleDeleteContact = async (rowIndex) => {
    if (!selectedFile) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/files/${selectedFile.id}/contacts/${rowIndex}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Contact supprimé');
        // Refresh contacts
        fetchContacts(selectedFile.id, contactsPagination.page, rowsPerPage, contactsSearch);
      }
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  // Handle file deletion
  const handleDeleteFile = async (fileId) => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Fichier supprimé');
        if (selectedFile?.id === fileId) {
          setSelectedFile(null);
          setContacts([]);
        }
        fetchFiles();
      }
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  // ==================== RCS CAPABILITY CHECK ====================
  
  // Check RCS capabilities for all contacts in file
  const handleCheckRcs = async () => {
    if (!selectedFile) return;
    
    setRcsCheckLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/files/${selectedFile.id}/check-rcs`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRcsCheckResults(data);
        setRcsActionDialogOpen(true);
        toast.success(`Vérification terminée: ${data.rcs_capable} RCS, ${data.sms_fallback} SMS`);
        // Refresh to show updated status
        fetchContacts(selectedFile.id, contactsPagination.page, rowsPerPage, contactsSearch);
      } else {
        toast.error('Erreur lors de la vérification RCS');
      }
    } catch (err) {
      console.error('RCS check error:', err);
      toast.error('Erreur lors de la vérification RCS');
    } finally {
      setRcsCheckLoading(false);
    }
  };

  // Apply RCS filter action
  const handleRcsAction = async (action) => {
    if (!selectedFile) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/contacts/files/${selectedFile.id}/filter-by-rcs?action=${action}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setRcsActionDialogOpen(false);
        // Refresh file and contacts
        fetchFiles();
        fetchContacts(selectedFile.id, 1, rowsPerPage, '');
      } else {
        toast.error('Erreur lors du filtrage');
      }
    } catch (err) {
      toast.error('Erreur lors du filtrage');
    }
  };

  // Get RCS status badge
  const getRcsStatusBadge = (contact) => {
    if (!contact.rcs_status) return null;
    
    if (contact.rcs_status === 'rcs') {
      return (
        <Badge className="bg-green-100 text-green-700 text-[10px] gap-0.5">
          <Smartphone className="w-2.5 h-2.5" />
          RCS
        </Badge>
      );
    } else if (contact.rcs_status === 'sms') {
      return (
        <Badge className="bg-orange-100 text-orange-700 text-[10px] gap-0.5">
          <MessageSquare className="w-2.5 h-2.5" />
          SMS
        </Badge>
      );
    }
    return null;
  };

  // Search contacts
  const handleSearch = (e) => {
    e.preventDefault();
    if (selectedFile) {
      fetchContacts(selectedFile.id, 1, rowsPerPage, contactsSearch);
    }
  };

  // Pagination
  const handleContactsPageChange = (newPage) => {
    if (selectedFile) {
      fetchContacts(selectedFile.id, newPage, rowsPerPage, contactsSearch);
    }
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    if (selectedFile) {
      fetchContacts(selectedFile.id, 1, parseInt(value), contactsSearch);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="contacts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contacts</h1>
            <p className="text-slate-500">Importez et gérez vos listes de contacts</p>
          </div>
          <Button 
            onClick={() => setImportModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="import-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer un fichier
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Files List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4" />
                Fichiers importés
              </CardTitle>
              <CardDescription>{files.length} fichier(s)</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {filesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-8">
                    <FileSpreadsheet className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Aucun fichier importé</p>
                    <Button 
                      variant="link" 
                      className="mt-2 text-indigo-600"
                      onClick={() => setImportModalOpen(true)}
                    >
                      Importer votre premier fichier
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {files.map(file => (
                      <div
                        key={file.id}
                        onClick={() => handleSelectFile(file)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedFile?.id === file.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 hover:border-indigo-200 hover:bg-slate-50'
                        }`}
                        data-testid={`file-item-${file.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{file.name}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge variant="secondary" className="text-[10px]">
                                <Users className="w-2.5 h-2.5 mr-1" />
                                {file.total_contacts}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={`text-[10px] ${file.valid_contacts === file.total_contacts ? 'text-green-600' : 'text-amber-600'}`}
                              >
                                {file.valid_contacts} valides
                              </Badge>
                              {file.rcs_check_done && (
                                <>
                                  <Badge className="bg-green-100 text-green-700 text-[10px]">
                                    <Smartphone className="w-2.5 h-2.5 mr-0.5" />
                                    {file.rcs_capable_count}
                                  </Badge>
                                  <Badge className="bg-orange-100 text-orange-700 text-[10px]">
                                    <MessageSquare className="w-2.5 h-2.5 mr-0.5" />
                                    {file.sms_fallback_count}
                                  </Badge>
                                </>
                              )}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {new Date(file.created_at).toLocaleDateString('fr-FR')}
                              {file.rcs_check_done && ' • RCS vérifié'}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFile(file.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Contacts Table */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {selectedFile ? selectedFile.name : 'Sélectionnez un fichier'}
                  </CardTitle>
                  {selectedFile && (
                    <CardDescription className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {selectedFile.valid_contacts} valides
                      </span>
                      <span className="flex items-center gap-1">
                        <XCircle className="w-3 h-3 text-red-500" />
                        {selectedFile.invalid_contacts} invalides
                      </span>
                      {selectedFile.rcs_check_done && (
                        <>
                          <span className="text-slate-400">•</span>
                          <span className="flex items-center gap-1 text-green-600">
                            <Smartphone className="w-3 h-3" />
                            {selectedFile.rcs_capable_count} RCS
                          </span>
                          <span className="flex items-center gap-1 text-orange-600">
                            <MessageSquare className="w-3 h-3" />
                            {selectedFile.sms_fallback_count} SMS
                          </span>
                        </>
                      )}
                      <span className="text-slate-400">•</span>
                      <span>{selectedFile.columns?.length} colonnes</span>
                    </CardDescription>
                  )}
                </div>
                
                {selectedFile && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant={selectedFile.rcs_check_done ? "outline" : "default"}
                      size="sm"
                      onClick={handleCheckRcs}
                      disabled={rcsCheckLoading || selectedFile.valid_contacts === 0}
                      className={!selectedFile.rcs_check_done ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                      data-testid="check-rcs-btn"
                    >
                      {rcsCheckLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      {selectedFile.rcs_check_done ? 'Revérifier RCS' : 'Vérifier RCS'}
                    </Button>
                    <form onSubmit={handleSearch} className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Rechercher..."
                        value={contactsSearch}
                        onChange={(e) => setContactsSearch(e.target.value)}
                        className="pl-8 h-8 w-40 text-sm"
                        data-testid="search-contacts"
                      />
                    </form>
                    <Select value={rowsPerPage.toString()} onValueChange={handleRowsPerPageChange}>
                      <SelectTrigger className="h-8 w-20 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedFile ? (
                <div className="text-center py-16">
                  <FileSpreadsheet className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500">Sélectionnez un fichier pour voir les contacts</p>
                </div>
              ) : contactsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
              ) : (
                <>
                  {/* Table */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-600 w-10">#</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Valide</th>
                            {selectedFile.rcs_check_done && (
                              <th className="px-3 py-2 text-left font-medium text-slate-600">Canal</th>
                            )}
                            {selectedFile.columns?.map(col => (
                              <th key={col} className="px-3 py-2 text-left font-medium text-slate-600">
                                {col}
                                {col === selectedFile.mobile_column && (
                                  <Phone className="inline w-3 h-3 ml-1 text-indigo-500" />
                                )}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-left font-medium text-slate-600 w-16">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contacts.map((contact, idx) => (
                            <tr 
                              key={contact.row_index} 
                              className={`border-b last:border-0 ${
                                contact.is_duplicate ? 'bg-amber-50' : 
                                !contact.is_valid ? 'bg-red-50' :
                                contact.rcs_status === 'sms' ? 'bg-orange-50/50' :
                                'hover:bg-slate-50'
                              }`}
                            >
                              <td className="px-3 py-2 text-slate-400 text-xs">
                                {contact.row_index + 1}
                              </td>
                              <td className="px-3 py-2">
                                {contact.is_valid ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-red-500" />
                                )}
                              </td>
                              {selectedFile.rcs_check_done && (
                                <td className="px-3 py-2">
                                  {getRcsStatusBadge(contact)}
                                </td>
                              )}
                              {selectedFile.columns?.map(col => (
                                <td key={col} className="px-3 py-2">
                                  {editingCell?.rowIndex === contact.row_index && editingCell?.column === col ? (
                                    <div className="flex items-center gap-1">
                                      <Input
                                        ref={editInputRef}
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') saveEdit();
                                          if (e.key === 'Escape') cancelEdit();
                                        }}
                                        className="h-7 text-sm py-0"
                                        autoFocus
                                      />
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={saveEdit}>
                                        <Check className="w-3 h-3 text-green-600" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}>
                                        <X className="w-3 h-3 text-red-600" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <span
                                      className="cursor-pointer hover:bg-indigo-50 px-1 py-0.5 rounded"
                                      onClick={() => startEditing(contact.row_index, col, contact.data?.[col])}
                                    >
                                      {contact.data?.[col] || <span className="text-slate-300">—</span>}
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td className="px-3 py-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
                                  onClick={() => handleDeleteContact(contact.row_index)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {contactsPagination.pages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-xs text-slate-500">
                        Page {contactsPagination.page} sur {contactsPagination.pages} ({contactsPagination.total} contacts)
                      </p>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={contactsPagination.page === 1}
                          onClick={() => handleContactsPageChange(contactsPagination.page - 1)}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={contactsPagination.page === contactsPagination.pages}
                          onClick={() => handleContactsPageChange(contactsPagination.page + 1)}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Import Modal */}
        <Dialog open={importModalOpen} onOpenChange={(open) => {
          setImportModalOpen(open);
          if (!open) resetImportState();
        }}>
          <DialogContent className="max-w-3xl" data-testid="import-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                Importer des contacts
              </DialogTitle>
              <DialogDescription>
                Importez vos contacts depuis un fichier CSV ou Excel
              </DialogDescription>
            </DialogHeader>

            {/* Upload Step */}
            {importStep === 'upload' && (
              <div className="py-8">
                <label className="block">
                  <div className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/50 transition-all">
                    <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="font-medium text-slate-700">Cliquez pour sélectionner un fichier</p>
                    <p className="text-sm text-slate-500 mt-1">CSV ou Excel (.xlsx)</p>
                    <p className="text-xs text-slate-400 mt-2">
                      La colonne "mobile" est obligatoire (numéros au format +33...)
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                </label>
                
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Format attendu :</p>
                  <div className="font-mono text-xs bg-white p-3 rounded border overflow-x-auto">
                    <table className="text-slate-600">
                      <thead>
                        <tr className="border-b">
                          <th className="px-3 py-1 text-left">mobile</th>
                          <th className="px-3 py-1 text-left">prenom</th>
                          <th className="px-3 py-1 text-left">nom</th>
                          <th className="px-3 py-1 text-left">...</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="px-3 py-1">+33612345678</td>
                          <td className="px-3 py-1">Jean</td>
                          <td className="px-3 py-1">Dupont</td>
                          <td className="px-3 py-1">...</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Step */}
            {importStep === 'preview' && importPreview && (
              <div className="space-y-4">
                {/* File Name */}
                <div>
                  <Label>Nom du fichier</Label>
                  <Input
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Nom pour identifier ce fichier"
                    className="mt-1"
                  />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-slate-900">{importPreview.total_rows}</p>
                    <p className="text-xs text-slate-500">Total lignes</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{importPreview.valid_in_preview}</p>
                    <p className="text-xs text-green-600">Valides (aperçu)</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">{importPreview.invalid_in_preview}</p>
                    <p className="text-xs text-red-600">Invalides (aperçu)</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-600">{importPreview.duplicates_count}</p>
                    <p className="text-xs text-amber-600">Doublons</p>
                  </div>
                </div>

                {/* Mobile column warning */}
                {!importPreview.mobile_column_found && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium text-red-700">Colonne "mobile" non trouvée</p>
                      <p className="text-sm text-red-600">Colonnes détectées : {importPreview.columns?.join(', ')}</p>
                    </div>
                  </div>
                )}

                {/* Columns */}
                <div>
                  <Label className="text-xs text-slate-500">Colonnes détectées</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {importPreview.columns?.map(col => (
                      <Badge 
                        key={col} 
                        variant={col === importPreview.mobile_column ? 'default' : 'secondary'}
                        className={col === importPreview.mobile_column ? 'bg-indigo-600' : ''}
                      >
                        {col === importPreview.mobile_column && <Phone className="w-3 h-3 mr-1" />}
                        {col}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Preview Table */}
                <div>
                  <Label className="text-xs text-slate-500">Aperçu (25 premières lignes)</Label>
                  <div className="border rounded-lg overflow-hidden mt-1">
                    <ScrollArea className="h-64">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-medium">Statut</th>
                            {importPreview.columns?.map(col => (
                              <th key={col} className="px-2 py-1.5 text-left font-medium">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.preview?.map((row, idx) => (
                            <tr 
                              key={idx} 
                              className={`border-b ${
                                row.is_duplicate ? 'bg-amber-50' : 
                                row.is_valid === false ? 'bg-red-50' : ''
                              }`}
                            >
                              <td className="px-2 py-1.5">
                                {row.is_valid ? (
                                  <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-red-500" />
                                )}
                              </td>
                              {importPreview.columns?.map(col => (
                                <td key={col} className="px-2 py-1.5 truncate max-w-[150px]">
                                  {row.data?.[col] || ''}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            )}

            {/* Importing Step */}
            {importStep === 'importing' && (
              <div className="py-12 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto" />
                <p className="mt-4 font-medium">Import en cours...</p>
                <p className="text-sm text-slate-500">Veuillez patienter</p>
              </div>
            )}

            <DialogFooter>
              {importStep === 'upload' && (
                <Button variant="outline" onClick={() => setImportModalOpen(false)}>
                  Annuler
                </Button>
              )}
              {importStep === 'preview' && (
                <>
                  <Button variant="outline" onClick={resetImportState}>
                    Retour
                  </Button>
                  <Button 
                    onClick={handleProceedImport}
                    disabled={!importPreview?.mobile_column_found || importLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="confirm-import-btn"
                  >
                    {importLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Importer {importPreview?.total_rows} contacts
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate Dialog */}
        <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <DialogContent data-testid="duplicate-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Doublons détectés
              </DialogTitle>
              <DialogDescription>
                {importPreview?.duplicates_count} numéro(s) en double ont été détectés dans votre fichier.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-slate-600 mb-4">
                Souhaitez-vous supprimer les doublons ? Seule la première occurrence de chaque numéro sera conservée.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  className="h-auto py-4 flex flex-col items-center gap-2"
                  onClick={() => handleImport(false)}
                  data-testid="keep-duplicates-btn"
                >
                  <X className="w-6 h-6 text-slate-500" />
                  <span className="font-medium">NON</span>
                  <span className="text-xs text-slate-500">Conserver les doublons</span>
                </Button>
                <Button
                  className="h-auto py-4 flex flex-col items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => handleImport(true)}
                  data-testid="remove-duplicates-btn"
                >
                  <Check className="w-6 h-6" />
                  <span className="font-medium">OUI</span>
                  <span className="text-xs">Supprimer les doublons</span>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* RCS Action Dialog */}
        <Dialog open={rcsActionDialogOpen} onOpenChange={setRcsActionDialogOpen}>
          <DialogContent className="max-w-md" data-testid="rcs-action-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-indigo-600" />
                Résultats de la vérification RCS
              </DialogTitle>
              <DialogDescription>
                Choisissez comment traiter les contacts selon leur compatibilité
              </DialogDescription>
            </DialogHeader>
            
            {rcsCheckResults && (
              <div className="space-y-4 py-4">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
                    <Smartphone className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-green-700">{rcsCheckResults.rcs_capable}</p>
                    <p className="text-sm text-green-600">Compatible RCS</p>
                    <p className="text-xs text-green-500 mt-1">{rcsCheckResults.rcs_percentage}%</p>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-200">
                    <MessageSquare className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-orange-700">{rcsCheckResults.sms_fallback}</p>
                    <p className="text-sm text-orange-600">SMS Fallback</p>
                    <p className="text-xs text-orange-500 mt-1">{100 - rcsCheckResults.rcs_percentage}%</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Que souhaitez-vous faire ?</p>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleRcsAction('keep_rcs_only')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Exclure les non-RCS</p>
                        <p className="text-xs text-slate-500">Supprimer les {rcsCheckResults.sms_fallback} contacts non compatibles</p>
                      </div>
                    </div>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleRcsAction('mark_sms')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Marquer pour SMS</p>
                        <p className="text-xs text-slate-500">Conserver tous, envoyer en SMS aux non-RCS</p>
                      </div>
                    </div>
                  </Button>
                  
                  <Button
                    variant="outline"
                    className="w-full justify-start h-auto py-3 px-4"
                    onClick={() => handleRcsAction('keep_all')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Users className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Conserver tous</p>
                        <p className="text-xs text-slate-500">Garder tous les contacts sans modification</p>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
